import { gql, useQuery } from '@apollo/client';
import type { BugReport, Chat, Message, MessageMention, MessageType, User } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { head, isNil } from 'lodash';
import { useCallback } from 'react';
import { useSupabase } from '../use-supabase';
import { usePaginatedQuery } from './use-paginated-query';

const GET_MESSAGES = gql`
  query GetMessages($chatId: UUID!, $first: Int!, $after: Cursor) {
    messaging_messagesCollection(
      first: $first
      after: $after
      filter: { chat_id: { eq: $chatId }, deleted_at: { is: NULL } }
      orderBy: { created_at: DescNullsLast }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          message_type
          body
          messager_user_id
          reply_to_message_id
          chat_id
          bug_report_id
          created_at
          messaging_mentionsCollection {
            edges {
              xsnode {
                __typename
                mentioned_user_id
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

type MessageWithMentions = Omit<Message, 'messaging_mentions'> & {
  messaging_mentionsCollection: {
    edges: {
      node: Pick<MessageMention, 'mentioned_user_id'>;
    }[];
  };
};

export const useMessages = ({ chatId }: { chatId: Uuid<Chat> }) => {
  const supabase = useSupabase();
  const {
    items: messages,
    loadMore,
    hasMore,
    loading,
    error,
  } = usePaginatedQuery<MessageWithMentions, { chatId: Uuid<Chat> }>(
    GET_MESSAGES,
    { chatId },
    'messaging_messagesCollection',
    false,
    20,
  );

  const mappedMessages = messages.map((message) => {
    return {
      ...message,
      messaging_mentions:
        message.messaging_mentionsCollection.edges.map((mentionEdge) => mentionEdge.node) ?? [],
    };
  });

  const sendMessage = useCallback(
    async (
      messageType: MessageType,
      fks: { chatId: Uuid<Chat> } | { bugReportId: Uuid<BugReport> },
      body: string,
      mentions: Uuid<User>[] = [],
      replyToMessageId: Uuid<Message> | null = null,
    ) => {
      const { data } = await supabase.auth.getUser();

      if (isNil(data.user)) {
        return;
      }

      const message = {
        message_type: messageType,
        body,
        messager_user_id: data.user.id as Uuid<User>,
        reply_to_message_id: replyToMessageId,
        ...fks,
      } as const;

      const { data: messageData, error: messageError } = await supabase
        .from('messaging_messages')
        .insert([message])
        .select()
        .single<Message>();

      if (messageError || isNil(messageData)) {
        console.error('Error sending message:', messageError);
        return;
      }

      // Insert mentions
      if (mentions.length > 0) {
        const mentionsData = mentions.map((userId) => ({
          message_id: messageData.id,
          mentioned_user_id: userId,
        }));

        const { error: mentionsError } = await supabase
          .from('messaging_mentions')
          .insert(mentionsData);

        if (mentionsError) {
          console.error('Error creating message mentions:', mentionsError);
        }
      }
    },
    [supabase],
  );

  return {
    messages: mappedMessages,
    loading,
    loadMore,
    hasMore,
    error,
    sendMessage,
  };
};

const GET_MESSAGE_BY_ID = gql`
  query GetMessageById($messageId: UUID!) {
    messaging_messagesCollection(filter: { id: { eq: $messageId }, deleted_at: { is: NULL } }) {
      edges {
        cursor
        node {
          __typename
          id
          message_type
          body
          messager_user_id
          reply_to_message_id
          chat_id
          bug_report_id
          created_at
          messaging_mentionsCollection {
            edges {
              node {
                __typename
                mentioned_user_id
              }
            }
          }
        }
      }
    }
  }
`;

export const useGetMessageById = (messageId?: Uuid<Message>) => {
  const { data, loading, error } = useQuery<
    { messaging_messagesCollection: { edges: { node: MessageWithMentions }[] } },
    { messageId?: Uuid<Message> }
  >(GET_MESSAGE_BY_ID, {
    variables: { messageId },
    skip: isNil(messageId),
  });

  const message = head(data?.messaging_messagesCollection.edges)?.node;

  const mappedMessage = isNil(message)
    ? null
    : {
        ...message,
        messaging_mentions:
          message.messaging_mentionsCollection.edges.map((mentionEdge) => mentionEdge.node) ?? [],
      };

  return {
    message: mappedMessage,
    loading,
    error,
  };
};
